import BrettO3 from "../object3d/Brett";
import Commons from "oc-commons/Commons";
import {v3} from "oc-threejs/short3";

// Muss ein Babel-Bug sein, aber das folgende klappt nicht für Typechecks, HasLocalCoordinates wird immer als undefined exportiert...
// import type {HasLocalCoordinates} from "./HasLocalCoordinates";

export default class Brett {

    // persistent (UserLayer) ------------------------------------------

    // transient (CompiledLayer) ---------------------------------------

    parent: ?Object;
    // die Box im lokalen System des Parents
    box = new THREE.Box3();
    // die Box im lokalen System des Schranks
    schrankBox = new THREE.Box3();
    size = v3();
    object3d: ?THREE.Object3D;
    product: ?Object;
    flaechenMass = v3();
    mainAxes: string;
    element: Element;
    visible = true;
    wireframe = false;
    fixedMaterialKey: string = null;
    inSketch = true;
    raycastVisibleTags = null;

    // Für Korpusbretter, welche Interieurs umschließen: wie viele Fronten grenzen an das Brett? 0, 1 oder 2
    // dieser Wert wird zur Zeit nicht benutzt, wäre aber relevant, sobald Fronten randlos abschließen sollen
    // wie bei so IKEA
    frontCount = 0;

    // Für Böden/Deckel wird hier die Anzahl der anliegenden Interiors gezählt, die auf den Boden angewiesen sind
    interieurCount = 0;

    // Für Korpusbretter, welche Interieurs umschließen:
    // true, wenn es eine innere Seitenwand oder ein Konstruktionsboden ist
    isInner = false;

    // die Anzahl der Vertices kann variieren
    vertices: THREE.Vector3[] = [];

    geometry = null; // optional, wenn gesetzt, dann wird direkt diese Geometrie genommen

    constructor(parent, artikelnummer, mainAxes = 'xz') {
        this.parent = parent;
        this.product = APP.findProduct(artikelnummer, true);
        this.mainAxes = mainAxes;

        // xyzSchema sollte immutable sein
        this.xyzSchema = Brett.XYZ_SCHEMA_DEFAULT;
    }

    compile() {
        if (this.parent) {
            this.schrankBox.min = this.parent.localToSchrank(this.box.min);
            this.schrankBox.max = this.parent.localToSchrank(this.box.max);
        }
        APP.model.lazyCreateObject3d(this, () => {
            const o3 = new BrettO3(this.product, this, APP.model);
            this.parent.child3dContainer.add(o3);
            return o3;
        });
    }

    get schrank() {
        return this.parent.schrank;
    }

    get arrangement() {
        return this.schrank.arrangement;
    }

    get materialArtikelnummer() {
        return APP.getMaterialForVsData(this);
    }

    get materialKeyOrArtikelnummer() {
        return this.fixedMaterialKey !== null ? this.fixedMaterialKey : this.materialArtikelnummer;
    }

    getMaterialProduct() {
        if (!Commons.isEmpty(this.product)) {
            return APP.findProduct(this.materialArtikelnummer);
        } else {
            return null;
        }
    }

    /**
     * muss nach dem manuellen Setzen der box aufgerufen werden!
     */
    boxToVertices() {
        this._setVerticesLength(8);
        this.vertices.forEach((v, vi) => this._setVertexFromBox(vi, v));
        this._updateSize();
    }

    /**
     * muss manuell nach Änderungen der edges / faces aufgerufen werden!
     */
    updateBox() {
        this.box.setFromPoints(this.vertices);
        this._updateSize();
    }

    /**
     * prüft, ob das Brett in der Ebene senkrecht zur gegebenen Achse plan liegt (also nicht geneigt)
     */
    isPlanar(axe = 'y', epsilon = 0.00001) {
        const axeIndex = 'xyz'.indexOf(axe);
        const ys = [null, null];
        return this.vertices.every((v, i) => {
            const j = this.xyzSchema[i][axeIndex];
            const w = v[axe];
            if (ys[j] === null) {
                ys[j] = w;
            }
            return Math.abs(ys[j] - w) < epsilon;
        });
    }

    /**
     * Gibt die Kante in X-Richtung zurück, die durch den Punkt an der Würfel-Koordinate z,y geht.
     * xEdgeZY(0,0) ist die hintere untere Kante, xEdgeXY(1,1) ist die vordere obere Kante.
     * Die Kante hat y,z - Koordinaten, die einzeln gesetzt oder gelesen werden können.
     * Zusätzlich kann auf _ ein 2D-Vektor (y,z) zugewiesen werden.
     * Die Zuweisungen erfolgen auf beide Vertices der Kante.
     */
    xEdgeZY(z, y) {
        return this._edge(2, 1, z, y);
    }

    /**
     * Gibt die Kante in Y-Richtung zurück, die durch den Punkt an der Würfel-Koordinate x,z geht.
     * yEdgeXZ(0,0) ist die linke hintere Kante, xEdgeXY(1,1) ist die rechte vordere Kante.
     * Die Kante hat x,z - Koordinaten, die einzeln gesetzt oder gelesen werden können.
     * Zusätzlich kann auf _ ein 2D-Vektor (x,z) zugewiesen werden.
     * Die Zuweisungen erfolgen auf beide Vertices der Kante.
     */
    yEdgeXZ(x, z) {
        return this._edge(0, 2, x, z);
    }

    /**
     * Gibt die Kante in Z-Richtung zurück, die durch den Punkt an der Würfel-Koordinate x,y geht.
     * zEdgeXY(0,0) ist die linke untere Kante, zEdgeXY(1,1) ist die rechte obere Kante.
     * Die Kante hat x,y - Koordinaten, die einzeln gesetzt oder gelesen werden können.
     * Zusätzlich kann auf _ ein 2D-Vektor (x,y) zugewiesen werden.
     * Die Zuweisungen erfolgen auf beide Vertices der Kante.
     */
    zEdgeXY(x, y) {
        return this._edge(0, 1, x, y);
    }

    /**
     * gibt das Koordinatensystem des Brettes zurück
     */
    getBasis(): THREE.Matrix4 {
        const x = this.xEdgeZY(0, 0).direction();
        const y = this.yEdgeXZ(0, 1).direction();
        const z = this.zEdgeXY(0, 0).direction();
        const makeBasis = (...b) => new THREE.Matrix4().makeBasis(...b);
        switch (this.mainAxes) {
            case 'xz':
                return makeBasis(x, y, z);
            case 'yz':
                return makeBasis(y, x.negate(), z);
            case 'yx':
                return makeBasis(y, z, x.negate());
            case 'xy':
                return makeBasis(x, z, y.negate());
        }
        throw 'unknown mainAxes ' + this.mainAxes;
    }

    getPointsInLocalCoordinates() {
        const inverseBasis = new THREE.Matrix4().copy(this.getBasis()).invert();
        return this.vertices.map(v => v.clone().applyMatrix4(inverseBasis));
    }

    getLocalBoundingBox() {
        return new THREE.Box3().setFromPoints(this.getPointsInLocalCoordinates());
    }

    toJSON() {
        return {
            box: this.box,
            size: this.size,
            Artikelnummer: this.product.Artikelnummer,
            InternerName: this.product.InternerName,
            flaechenMass: this.flaechenMass,
        };
    }

    get2DPoints() {
        const result = [];

        result.push(this.get2DPoint(0));
        result.push(this.get2DPoint(2));
        if (this.vertices.length > 8) {
            result.push(this.get2DPoint(8));
        }
        result.push(this.get2DPoint(6));
        result.push(this.get2DPoint(4));

        return result;
    }

    get2DPoint(i) {
        return {
            x : this.vertices[i].x,
            y: this.vertices[i].y
        }
    }


    getFrontGeometry() {
        const points = this.get2DPoints();

        var shape = new THREE.Shape();

        const last = points[points.length-1];
        shape.moveTo(last.x, last.y);
        points.forEach(p => {
            shape.lineTo(p.x, p.y);
        })

        return new THREE.ShapeGeometry(shape);
    }



    _axis(index) {
        return 'xyz'.charAt(index);
    }

    _edge(axisIndexA, axisIndexB, ai, bi) {
        const axisA = this._axis(axisIndexA);
        const axisB = this._axis(axisIndexB);
        const THIS = this;
        const ii = [];
        this.xyzSchema.forEach((r, ri) => {
            if ((ai === 2 ? (r[axisIndexA] >= 2) : r[axisIndexA] === ai) &&
                (bi === 2 ? (r[axisIndexB] >= 2) : r[axisIndexB] === bi)) {
                ii.push(ri);
            }
        });
        return {
            direction() {
                return THIS._vertexFromIndex(ii[1]).clone().sub(THIS._vertexFromIndex(ii[0])).normalize();
            },
            set _(v) {
                ii.forEach(i => {
                    THIS._vertexFromIndex(i)[axisA] = v[axisA];
                    THIS._vertexFromIndex(i)[axisB] = v[axisB];
                });
            },
            get [axisA]() {
                return THIS._vertexFromIndex(ii[0])[axisA];
            },
            set [axisA](v) {
                ii.forEach(i => THIS._vertexFromIndex(i)[axisA] = v)
            },
            get [axisB]() {
                return THIS._vertexFromIndex(ii[0])[axisB];
            },
            set [axisB](v) {
                ii.forEach(i => THIS._vertexFromIndex(i)[axisB] = v)
            },
        };
    }

    _vertexFromIndex(i) {
        this._setVerticesLength(Math.max(this.vertices.length, i + 1));
        return this.vertices[i];
    }

    _vertexFromXYZ(x, y, z) {
        const ri = this.xyzSchema.findIndex((r, ri) =>
            x === r[0] && y === r[1] && z === r[2]);
        return ri === -1 ? null : this.vertices[ri];
    }

    _setVerticesLength(size) {
        Commons.setArrayLength(this.vertices, size, i => this._setVertexFromBox(i, v3()));
    }

    _setVertexFromBox(i, vertex) {
        this.xyzSchema[i].forEach((c, ci) => {
            const axis = this._axis(ci);
            vertex[axis] = this.box[c % 2 === 0 ? 'min' : 'max'][axis];
        });
        return vertex;
    }

    _updateSize() {
        this.box.getSize(this.size);
        this._updateFlaechenmass();
    }

    _updateFlaechenmass() {
        this.flaechenMass = this.getLocalBoundingBox().getSize();
    }

    static XYZ_SCHEMA_DEFAULT = [
        [0, 0, 0],
        [0, 0, 1],
        [0, 1, 0],
        [0, 1, 1],
        [1, 0, 0],
        [1, 0, 1],
        [1, 1, 0],
        [1, 1, 1],
        // die folgenden beiden Punkte werden sowohl für X als auch für Z-edges genommen
        // und liegen jeweils in der Mitte der oberen Seite
        [2, 1, 2],
        [3, 1, 3],
    ];

    iterate(iterator) {
        iterator(this);
    }

}
